/**
 * Tratamento padronizado de erros da API
 */

/**
 * Parse de erros da API em formato padronizado
 * @param {Response|Error|Object} error - Erro retornado pela API ou erro de rede
 * @returns {Object} Objeto com mensagem e detalhes do erro
 */
export async function parseApiError(error) {
    // Se for um Response object
    if (error && typeof error.json === 'function') {
        try {
            const data = await error.json();
            return {
                message: data.message || 'Erro ao processar requisição',
                details: data.exceptionDetails || [],
                status: error.status,
            };
        } catch (e) {
            return {
                message: 'Erro ao processar resposta do servidor',
                details: [],
                status: error.status,
            };
        }
    }
    
    // Se for um objeto de erro já parseado (ex.: throw await parseApiError(response))
    if (error && typeof error === 'object' && error.message) {
        return {
            message: error.message,
            details: error.details ?? error.exceptionDetails ?? [],
            status: error.status,
        };
    }
    
    // Erro de rede ou desconhecido
    return {
        message: error?.message || 'Erro ao conectar com o servidor',
        details: [],
        status: null,
    };
}

/**
 * Converte detalhes de erro da API em erros por campo e mensagens sem localização.
 * @param {Array} exceptionDetails - Array de detalhes (reason, location opcional)
 * @param {Object} [options]
 * @param {function(string): string|null} [options.mapLocation] - Normaliza o nome do campo
 * @returns {{ fieldErrors: Object, additionalMessages: string[] }}
 */
export function extractFieldErrors(exceptionDetails = [], options = {}) {
    const { mapLocation } = options;
    const fieldErrors = {};
    const additionalMessages = [];

    (exceptionDetails || []).forEach((detail) => {
        const reason = detail?.reason || detail?.message || 'Campo inválido';
        if (!detail?.location) {
            if (reason) {
                additionalMessages.push(reason);
            }
            return;
        }

        const raw = detail.location;
        const field = mapLocation ? mapLocation(raw) : raw.split('.').pop();
        if (!field) {
            additionalMessages.push(reason);
            return;
        }

        if (fieldErrors[field]) {
            fieldErrors[field] = `${fieldErrors[field]}\n${reason}`;
        } else {
            fieldErrors[field] = reason;
        }
    });

    return { fieldErrors, additionalMessages };
}

/**
 * Gera mensagem amigável para o usuário
 * @param {Object} error - Objeto de erro parseado
 * @returns {string} Mensagem amigável
 */
export function getFriendlyErrorMessage(error) {
    const apiMessage = typeof error?.message === 'string' ? error.message.trim() : '';
    if (apiMessage) {
        return apiMessage;
    }

    if (error.status === 400) {
        return 'Dados inválidos. Verifique os campos destacados.';
    }
    if (error.status === 401) {
        return 'Sessão expirada. Por favor, faça login novamente.';
    }
    if (error.status === 403) {
        return 'Você não tem permissão para realizar esta ação.';
    }
    if (error.status === 404) {
        return 'Registro não encontrado.';
    }
    if (error.status === 500) {
        return 'Erro interno do servidor. Tente novamente mais tarde.';
    }
    
    return error.message || 'Ocorreu um erro inesperado.';
}

/**
 * Log de erro (pode ser estendido para enviar para serviço de monitoramento)
 * @param {Object} error - Objeto de erro
 * @param {string} context - Contexto onde o erro ocorreu
 */
export function logError(error, context = '') {
    // Aqui pode ser adicionado envio para serviço de monitoramento (Sentry, etc.)
}
